import {
  computed,
  getCurrentInstance,
  onScopeDispose,
  ref,
  set,
  watch,
} from 'vue';
import { useEventListener, useOnline } from '@vueuse/core';
import RestaurantsService from '@/api/services/restaurants.service';
import StorefrontCatalogService from '@/api/services/storefront-catalog.service';
import { applyStoreLook } from '@/composition/design/store-look';
import { useConfirmDialog } from '@/composition/use-confirm-dialog';
import { useHideHelpBubble } from '@/composition/use-hide-help-bubble';
import { useUi } from '@/composition/use-ui';
import {
  applyPreset,
  cloneDraft,
  draftFromRestaurant,
  isBrandHex,
  isDirty,
  normalizeBrand,
  toSavePayload,
} from '@/domain/design/design-draft';
import type { IDesignDraft } from '@/domain/design/design-draft';
import { presetOptions } from '@/domain/design/style-catalog';
import { AlertType } from '@/interfaces/IAlert';
import type {
  IFocalPoint,
  IStyleCatalogResponse,
  IStyleMode,
} from '@/interfaces/IStorefrontStyle';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { registerReusedPageLeaveGuard } from '@/utils/navigation.utils';

type ILeaveGuard = () => Promise<boolean>;
/** The mounted page's question, for its beforeRouteLeave (single-instance page). */
let activeGuard: ILeaveGuard | null = null;

/** The «Diseño y estilo» page's answer to «may the router leave?». */
export function confirmDesignLeave(): Promise<boolean> {
  return activeGuard ? activeGuard() : Promise.resolve(true);
}

export type ILoadStatus = 'loading' | 'ready' | 'error';
export type ICatalogStatus = ILoadStatus;

/**
 * «Diseño y estilo» (Storefront V2 ADM-3): the draft look of the store, the
 * styles catalog read from the storefront, the save and the leave guard.
 * Rules live in `src/domain/design/*`; this only wires them to the stores.
 */
export function useDesignPage() {
  const restaurantStore = useRestaurantStore();
  const ui = useUi();
  const confirmDialog = useConfirmDialog();
  const online = useOnline();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  /** Labels exist in the bundled en catalog, so `en` decides (vue-i18n 8 `$te(key, locale)`). */
  const known = (key: string) => proxy.$te(key, 'en');

  const saved = ref<IDesignDraft | null>(null);
  const draft = ref<IDesignDraft | null>(null);
  const lookStatus = ref<ILoadStatus>('loading');
  const saving = ref(false);
  const catalog = ref<IStyleCatalogResponse | null>(null);
  const catalogStatus = ref<ICatalogStatus>('loading');
  let disposed = false;
  let lookRequest = 0;
  let pendingLeave: Promise<boolean> | null = null;
  /**
   * The owner's mode from before a preset with its own mode (Noche) replaced
   * it (`applyPreset`). Kept out of the draft: never saved, never dirty.
   */
  let modeBeforePreset: IStyleMode | null = null;

  const dirty = computed(
    () => !!saved.value && !!draft.value && isDirty(saved.value, draft.value)
  );

  /**
   * The store's saved look, read fresh from `/full` (the request the store
   * load uses) on mount, after a store switch on the same page and on
   * «Reintentar». The store's restaurant object may be stale or partial
   * (another page may have replaced it with a write's answer, such as the
   * bare `PUT /restaurants/:id` entity without `storefrontStyle`), and a
   * stale look must never become the saved state that the next save writes
   * for every diner: until the answer arrives there is no saved state, so
   * nothing can be saved.
   */
  async function loadLook() {
    const restaurant = restaurantStore.restaurant;
    const request = ++lookRequest;
    saved.value = null;
    draft.value = null;
    modeBeforePreset = null;
    lookStatus.value = 'loading';
    if (!restaurant) return;
    try {
      const fresh = await RestaurantsService.getFullRestaurantBySlug(
        restaurant.stringId
      );
      const current = restaurantStore.restaurant;
      if (disposed || request !== lookRequest || !current) return;
      if (!fresh || fresh.id !== current.id)
        throw new Error('The /full answer is not the current store');
      applyStoreLook(current, fresh);
      saved.value = draftFromRestaurant(fresh);
      draft.value = cloneDraft(saved.value);
      lookStatus.value = 'ready';
    } catch (error) {
      if (disposed || request !== lookRequest) return;
      console.error('[design] saved look', error);
      lookStatus.value = 'error';
    }
  }
  watch(
    () => restaurantStore.restaurant?.id ?? null,
    () => void loadLook(),
    { immediate: true }
  );

  async function loadCatalog() {
    catalogStatus.value = 'loading';
    try {
      const parsed = await StorefrontCatalogService.getStyleCatalog();
      if (disposed) return;
      if (!parsed) throw new Error('Unusable style catalog');
      catalog.value = parsed;
      catalogStatus.value = 'ready';
    } catch (error) {
      if (disposed) return;
      console.error('[design] style catalog', error);
      catalogStatus.value = 'error';
    }
  }
  void loadCatalog();

  const presetList = computed(() =>
    catalog.value
      ? presetOptions(catalog.value, (id) => ({
          name: known(`design.style.${id}.name`)
            ? t(`design.style.${id}.name`)
            : undefined,
          hint: known(`design.style.${id}.hint`)
            ? t(`design.style.${id}.hint`)
            : undefined,
        }))
      : []
  );

  function setPreset(id: string) {
    const preset = catalog.value?.presets.find((entry) => entry.id === id);
    if (!draft.value || !preset) return;
    const choice = applyPreset(draft.value, preset, modeBeforePreset);
    draft.value = choice.draft;
    modeBeforePreset = choice.modeBeforePreset;
  }

  function setBrand(hex: string) {
    if (draft.value && isBrandHex(hex))
      draft.value = { ...cloneDraft(draft.value), brand: normalizeBrand(hex) };
  }

  /** The owner's explicit mode wins over the one a preset would give back. */
  function setMode(mode: IStyleMode) {
    if (!draft.value || draft.value.mode === mode) return;
    modeBeforePreset = null;
    draft.value = { ...cloneDraft(draft.value), mode };
  }

  function setFocal(focal: IFocalPoint) {
    if (draft.value?.bannerFocal)
      draft.value = {
        ...cloneDraft(draft.value),
        bannerFocal: { x: focal.x, y: focal.y },
      };
  }

  function discard() {
    draft.value = saved.value ? cloneDraft(saved.value) : null;
    modeBeforePreset = null;
  }

  async function save() {
    const restaurant = restaurantStore.restaurant;
    const value = draft.value;
    if (
      !restaurant?.id ||
      !value ||
      saving.value ||
      !dirty.value ||
      !online.value
    )
      return;
    const rid = restaurant.id;
    saving.value = true;
    try {
      const response = await RestaurantsService.saveDesign(
        rid,
        toSavePayload(value)
      );
      const current = restaurantStore.restaurant;
      if (disposed || !current || current.id !== rid) return;
      // Vue 2: `set` keeps the new keys reactive for every reader of /full.
      set(current, 'themeSettings', response.themeSettings);
      set(current, 'storefrontStyle', response.storefrontStyle);
      set(current, 'bannerMeta', response.bannerMeta);
      // Changes made while saving stay in the draft (still dirty against the new saved state).
      saved.value = draftFromRestaurant(current);
      ui.showToast(AlertType.Success, t('design.saved'));
    } catch (error) {
      if (!disposed) ui.serverError(error, t('design.saveError'));
    } finally {
      if (!disposed) saving.value = false;
    }
  }

  async function askLeave(): Promise<boolean> {
    const accepted = await confirmDialog.confirm({
      title: t('design.leave.title'),
      message: t('design.leave.message'),
      acceptText: t('design.leave.accept'),
      tone: 'danger',
    });
    // One question per navigation: the draft counts as clean from here on.
    if (accepted && !disposed) discard();
    return accepted;
  }

  function confirmLeave(): Promise<boolean> {
    if (!dirty.value) return Promise.resolve(true);
    if (!pendingLeave)
      pendingLeave = askLeave().finally(() => {
        pendingLeave = null;
      });
    return pendingLeave;
  }

  activeGuard = confirmLeave;
  // A store switch on this page reuses it: the global guard asks first (`App.ts:928-941`).
  const unregister = registerReusedPageLeaveGuard(confirmLeave);
  useEventListener(window, 'beforeunload', (event: BeforeUnloadEvent) => {
    if (!dirty.value) return;
    event.preventDefault();
    event.returnValue = '';
  });
  useHideHelpBubble(dirty);
  onScopeDispose(() => {
    disposed = true;
    unregister();
    if (activeGuard === confirmLeave) activeGuard = null;
  });

  return {
    draft,
    saved,
    lookStatus,
    loadLook,
    dirty,
    saving,
    online,
    catalogStatus,
    presetOptions: presetList,
    loadCatalog,
    setPreset,
    setBrand,
    setMode,
    setFocal,
    save,
    discard,
  };
}
